import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Grid, Typography } from "@material-ui/core";
import { cloneDeep } from "lodash";
import Loader from "common/Loader";
import AppConstants from "../constants";
import GridWrapper from "../common/GridWrapper";

import { useStyle } from "./OnBoarding.styles";
import { AppState } from "../config/redux/reducers";
import DriverVerificationDetails from "./components/DriverVerificationDetails";
import { fetchPortalFilter } from "./redux/onBoardingSlice";
import { createOnBoardingFilterPayload, isTerminalDriverStatus } from "./utils/onboarding.transforms";
import type { PortalFilterElement } from "./redux/onBoardingSlice.types";


// ── Types ────────────────────────────────────────────────────────────────
interface OnBoardingPageState {
  userSearchValue: string;
  openSnackbar: boolean;
  snackbarType: string;
  snackbarMessage: string;
  currentRowData: PortalFilterElement;
  openEditStoreDialog: boolean;
  openViewQRCodeDialog: boolean;
  DriverVerificationpopup: boolean;
}

/** Stable reference — defined outside component so it never causes a re-render. */
const renderDataForSearchSection = (): null => null;

const OnBoardingPage = () => {

  const dispatch = useDispatch();
  const classes = useStyle();

  const { loading, driverList, driverFiltersDropdowns } = useSelector((state: AppState) => state.onBoarding);
  
  const {countryCode} = useSelector((state: AppState) => state.common);

  const [posListToShow, setPosListToShow] = useState<any[]>(driverList.elements);
  const [appState, setAppState] = useState<OnBoardingPageState>({
    userSearchValue: "",
    openSnackbar: false,
    snackbarType: AppConstants.SNACKBAR.TYPES.SUCCESS,
    snackbarMessage: "",
    currentRowData: {} as PortalFilterElement,
    openEditStoreDialog: false,
    openViewQRCodeDialog: false,
    DriverVerificationpopup: false,
  });

  const [tableWrapperData, setTableWrapperData] = useState({
    title: AppConstants.ON_BOARDING_CONSTANT.TABLE_WRAPPER_DATA.title,
    headerButtons: AppConstants.ON_BOARDING_CONSTANT.TABLE_WRAPPER_DATA.headerButtons,
    loadMore: { ...AppConstants.ON_BOARDING_CONSTANT.TABLE_WRAPPER_DATA.loadMore },
    HeaderData: AppConstants.ON_BOARDING_CONSTANT.TABLE_WRAPPER_DATA.HeaderData.map((header: any) => {
      if (header.field === "status") {
        return {
          ...header,
          renderCell: (params: any) =>
            params.value ? (
              <Grid className={classes.statusHeaderContainer} container alignItems="center">
                <Typography className={["tag", params.value.toUpperCase()].join(" ")}>
                  {params.value}
                </Typography>
              </Grid>
            ) : <></>,
        };
      }
      return header;
    }),
    defaultFiltersObj: AppConstants.ON_BOARDING_CONSTANT.TABLE_WRAPPER_DATA.defaultFiltersObj as any,
    SEARCH_TYPES: AppConstants.ON_BOARDING_CONSTANT.TABLE_WRAPPER_DATA.SEARCH_TYPES,
  });
  const [activeFilters, setActiveFilters] = useState(AppConstants.ON_BOARDING_CONSTANT.TABLE_WRAPPER_DATA.defaultFiltersObj as any);

  const driverListRef = useRef(driverList);
  useEffect(() => { driverListRef.current = driverList; }, [driverList]);

  const isMountedRef = useRef(false);

  const handleLoadMoreDisableState = useCallback(() => {
    const loadMoreState = tableWrapperData.loadMore;
    loadMoreState.disabled = driverList.totalPages ? driverList.pageNumber + 1 >= driverList.totalPages : true;
    setTableWrapperData((prev) => ({ ...prev, loadMore: { ...loadMoreState } }));
  }, [tableWrapperData, driverList]);

  const filterCallback = useCallback((filters: any, isLoadMore?: boolean) => {
    if (filters) {
      const payloadFilters = cloneDeep(filters);
      payloadFilters.pageSize = tableWrapperData.loadMore.pageSize;
      const payload = createOnBoardingFilterPayload(payloadFilters, driverListRef.current, isLoadMore);
      setActiveFilters(filters);
      dispatch(fetchPortalFilter({ payload }) as any);
    }
  }, [tableWrapperData.loadMore.pageSize, dispatch]);


  const searchFilterHandler = (data: { [key: string]: string }, updatedSearchValue: string, filters: any) => {
    setAppState((prev) => ({ ...prev, userSearchValue: updatedSearchValue }));
    if (updatedSearchValue) {
      const searchFilters = cloneDeep(filters);
      for (const searchKey in data) {
        if (data[searchKey]) {
          searchFilters.searchAttribute = { key: searchKey, value: data[searchKey] };
          filterCallback(searchFilters);
          return;
        }
      }
    } else if (filters) {
      const clearedFilters = cloneDeep(filters);
      delete clearedFilters.searchAttribute;
      filterCallback(clearedFilters);
    }
  };

    const closeConsignmentDetailsPopup = useCallback(() => {
      setAppState((prev) => ({ ...prev, DriverVerificationpopup: false, currentRowData: {} as PortalFilterElement }));
    }, []);

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    const defaultFilters = AppConstants.ON_BOARDING_CONSTANT.TABLE_WRAPPER_DATA.defaultFiltersObj;
    filterCallback(defaultFilters);
  }, [countryCode]);

  useEffect(() => {
    setPosListToShow(driverList.elements);
    handleLoadMoreDisableState();
  }, [driverList.elements]);

  useEffect(() => {
    setTableWrapperData((prev) => ({
      ...prev,
      HeaderData: prev.HeaderData.map((header: any) => {
        if (!header.filterObj) return header;
        if (header.filterObj.fieldName === "status") {
          return { ...header, filterObj: { ...header.filterObj, items: driverFiltersDropdowns.statusList || [] } };
        }
        if (header.filterObj.fieldName === "vehicleType") {
          return { ...header, filterObj: { ...header.filterObj, items: driverFiltersDropdowns.vehicleTypeList || [] } };
        }
        return header;
      }),
    }));
  }, [driverFiltersDropdowns]);

  // Removed redundant useEffect block referencing undefined 'params'

    const handleRowClick = useCallback((params: any) => {
      const rowObj = params.data as PortalFilterElement | undefined;
      if (!rowObj || isTerminalDriverStatus(rowObj.status)) return;
      setAppState((prev) => ({ ...prev, currentRowData: rowObj, DriverVerificationpopup: true }));
    }, []);

  const handleReject = useCallback((payload: any) => {
    setAppState((prev) => ({ ...prev, DriverVerificationpopup: false, currentRowData: { ...prev.currentRowData, rejectionPayload: payload } }));
    filterCallback(activeFilters);
  }, [activeFilters]);

  const handleApprove = useCallback(() => {
    setAppState((prev) => ({ ...prev, DriverVerificationpopup: false }));
    filterCallback(activeFilters);
  }, [activeFilters]);

  return (
    <div className={classes.root}>
      {loading && <Loader></Loader>}
      <Grid className={classes.container} container>
        <Grid className="userItem content" item>
          <GridWrapper
            showHeader={false}
            checkboxSelection={false}
            headerData={tableWrapperData.HeaderData}
            rowData={posListToShow}
            activeFilter={activeFilters}
            loading={loading}
            searchTypes={tableWrapperData.SEARCH_TYPES}
            defaultFilter={tableWrapperData.defaultFiltersObj}
            title={tableWrapperData.title}
            disableSelectionOnClick={true}
            searchValue={appState.userSearchValue}
            loadMore={{
              ...tableWrapperData.loadMore,
              rowCount: driverList.totalElements || 0,
            }}
            searchCallback={searchFilterHandler}
            filterGrid={filterCallback}
            showDataNextToSearchButton={true}
            renderDataForSearchSection={renderDataForSearchSection}
            showRefreshButton={true}
            onRowClick={handleRowClick}
            getRowClassName={(params: any) =>
              isTerminalDriverStatus(params.row?.status ?? "")
                ? classes.nonClickableRow
                : ""
            }
          />
        </Grid>
      </Grid>
      {appState.DriverVerificationpopup && (
        <DriverVerificationDetails
          open={appState.DriverVerificationpopup}
          data={appState.currentRowData}
          onClose={closeConsignmentDetailsPopup}
          onReject={handleReject}
          onApprove={handleApprove}
        />
      )}
    </div>
  );
};

export default React.memo(OnBoardingPage);
